import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ErrorLike } from '@apollo/client'
import { CombinedGraphQLErrors, isErrorLike } from '@apollo/client/errors'
import { skipToken, useQuery } from '@apollo/client/react'
import type { GraphQLFormattedError } from 'graphql'

import { useAccount } from '~publish/contexts/AccountContext'

import { NotesQuery, notesInput } from './notesQueries'
import type { CollaborationNote } from './notesQueries'
import { makeNoteSubject, noteSubjectKey } from './noteSubject'
import type { NoteSubject } from './noteSubject'

export type NotesFailure = 'permissionDenied' | 'unavailable' | 'requestFailed'

export type NotesResult =
  | { status: 'idle' | 'loading' }
  | { status: 'failed'; reason: NotesFailure; retry: () => Promise<void> }
  | {
      status: 'ready'
      notes: readonly CollaborationNote[]
      hasNextPage: boolean
      fetchingMore: boolean
      pageError: ErrorLike | undefined
      loadMore: () => Promise<void>
      loadUntilNote: (
        noteId: string,
      ) => Promise<'found' | 'missing' | 'failed' | 'cancelled'>
      refresh: () => Promise<void>
      refreshing: boolean
      warning: ErrorLike | undefined
      readBusy: boolean
      beginWrite: () => boolean
      endWrite: () => void
    }

type Selection = {
  accountId: string
  subject: NoteSubject
}

type ScopedActivity = {
  selection: Selection
  value: 'paging' | 'refreshing'
}

type ScopedError = {
  selection: Selection
  error: ErrorLike
}

type PendingAction = {
  selection: Selection
  controller: AbortController
  value: 'paging' | 'refreshing'
}

const isRootNotesError = (error: GraphQLFormattedError): boolean =>
  error.path?.length === 1 && error.path[0] === 'collaborationNotes'

const classifyNotesError = (error: ErrorLike): NotesFailure | undefined => {
  if (!CombinedGraphQLErrors.is(error)) return 'requestFailed'

  const rootErrors = error.errors.filter(isRootNotesError)
  if (rootErrors.length === 0) return undefined

  const codes = new Set(rootErrors.map((each) => each.extensions?.code))
  if (codes.has('FORBIDDEN')) return 'permissionDenied'
  if (codes.has('NOT_FOUND') || codes.has('BAD_USER_INPUT')) {
    return 'unavailable'
  }

  return 'requestFailed'
}

export const useNotes = (
  subject: NoteSubject | null,
  writePending = false,
): NotesResult => {
  const { account } = useAccount()
  const accountId = account?.id ?? null
  const subjectType = subject?.subjectType
  const subjectId = subject?.subjectId
  const selection = useMemo(
    (): Selection | null =>
      subjectType && subjectId && accountId
        ? { accountId, subject: makeNoteSubject(subjectType, subjectId) }
        : null,
    [accountId, subjectId, subjectType],
  )
  const selectionRef = useRef<Selection | null>(selection)
  selectionRef.current = selection
  const selectionKey = selection
    ? noteSubjectKey(selection.accountId, selection.subject)
    : null
  const previousSelectionKeyRef = useRef(selectionKey)
  const selectionChanged = previousSelectionKeyRef.current !== selectionKey
  previousSelectionKeyRef.current = selectionKey
  const deferredReadKeyRef = useRef<string | null>(null)
  if (writePending && selectionKey && selectionChanged) {
    deferredReadKeyRef.current = selectionKey
  } else if (deferredReadKeyRef.current !== selectionKey) {
    deferredReadKeyRef.current = null
  }
  const deferRead = writePending && deferredReadKeyRef.current === selectionKey
  const resumeFromWrite = deferredReadKeyRef.current === selectionKey

  const [activity, setActivity] = useState<ScopedActivity | null>(null)
  const [pageError, setPageError] = useState<ScopedError | null>(null)
  const [requestError, setRequestError] = useState<ScopedError | null>(null)
  const pendingActionRef = useRef<PendingAction | null>(null)
  const writeLockRef = useRef<Selection | null>(null)

  useEffect(
    (): (() => void) => (): void => {
      const action = pendingActionRef.current
      if (action?.selection === selection) {
        action.controller.abort()
        if (pendingActionRef.current === action) pendingActionRef.current = null
      }
      if (writeLockRef.current === selection) writeLockRef.current = null
    },
    [selection],
  )

  const query = useQuery(
    NotesQuery,
    selection && !deferRead
      ? {
          variables: {
            input: notesInput(selection.subject),
            first: 10,
            after: null,
          },
          fetchPolicy: resumeFromWrite ? 'cache-first' : 'cache-and-network',
          errorPolicy: 'all',
          notifyOnNetworkStatusChange: true,
          // Manual reobserves attach a one-use abort signal to the observable.
          // Reset that context when Apollo reobserves for a new selection.
          context: {},
        }
      : skipToken,
  )

  const requestFirstPage = useCallback(async (): Promise<void> => {
    if (!selection || selectionRef.current !== selection) return
    if (writeLockRef.current === selection) return
    if (query.loading) return
    if (pendingActionRef.current?.selection === selection) return

    const controller = new AbortController()
    const action: PendingAction = { selection, controller, value: 'refreshing' }
    pendingActionRef.current = action
    setActivity({ selection, value: 'refreshing' })

    try {
      const response = await query.observable.reobserve({
        variables: {
          input: notesInput(selection.subject),
          first: 10,
          after: null,
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
        context: {
          fetchOptions: { signal: controller.signal },
          queryDeduplication: false,
        },
      })
      if (selectionRef.current !== selection) return

      if (response.error) {
        if (
          !classifyNotesError(response.error) &&
          response.data?.collaborationNotes
        ) {
          setRequestError(null)
          setPageError(null)
        }
      } else {
        setRequestError(null)
        setPageError(null)
      }
    } catch {
      // Apollo updates query.error before reobserve rejects.
    } finally {
      if (pendingActionRef.current === action) pendingActionRef.current = null
      if (selectionRef.current === selection) {
        setActivity((current) =>
          current?.selection === selection && current.value === 'refreshing'
            ? null
            : current,
        )
      }
    }
  }, [query, selection])

  const connection =
    query.dataState === 'complete' ? query.data.collaborationNotes : undefined
  const endCursor = connection?.pageInfo.endCursor ?? null
  const hasNextPage = Boolean(connection?.pageInfo.hasNextPage && endCursor)

  const fetchPage = useCallback(
    async (after: string, first: number, controller: AbortController) => {
      if (!selection) return null
      const response = await query.fetchMore({
        errorPolicy: 'all',
        variables: {
          input: notesInput(selection.subject),
          first,
          after,
        },
        context: {
          fetchOptions: { signal: controller.signal },
          queryDeduplication: false,
        },
      })
      if (selectionRef.current !== selection || controller.signal.aborted) {
        return null
      }

      const page = response.data?.collaborationNotes
      const failure =
        response.error && CombinedGraphQLErrors.is(response.error)
          ? classifyNotesError(response.error)
          : undefined
      if (response.error) {
        if (failure) {
          setRequestError({ selection, error: response.error })
        } else if (page) {
          setRequestError({ selection, error: response.error })
          setPageError(null)
        } else {
          setPageError({ selection, error: response.error })
        }
      } else {
        setRequestError(null)
        setPageError(null)
      }
      return { page, failed: Boolean(response.error && (failure || !page)) }
    },
    [query, selection],
  )

  const loadPages = useCallback(
    async (
      noteId?: string,
    ): Promise<'found' | 'missing' | 'failed' | 'cancelled'> => {
      if (noteId && connection?.edges.some(({ node }) => node.id === noteId)) {
        return 'found'
      }
      if (!selection || !endCursor || !hasNextPage) return 'missing'
      if (
        selectionRef.current !== selection ||
        writeLockRef.current === selection ||
        query.loading ||
        pendingActionRef.current?.selection === selection
      ) {
        return 'cancelled'
      }

      const controller = new AbortController()
      const action: PendingAction = { selection, controller, value: 'paging' }
      pendingActionRef.current = action
      setActivity({ selection, value: 'paging' })
      let cursor: string | null = endCursor
      try {
        while (cursor && !controller.signal.aborted) {
          // The next cursor is only available after this page returns.
          // eslint-disable-next-line no-await-in-loop
          const response = await fetchPage(
            cursor,
            noteId ? 100 : 10,
            controller,
          )
          if (selectionRef.current !== selection || controller.signal.aborted) {
            return 'cancelled'
          }
          const page = response?.page
          if (response?.failed || !page) return 'failed'
          if (!noteId || page.edges.some(({ node }) => node.id === noteId)) {
            return 'found'
          }
          const nextCursor = page.pageInfo.endCursor
          if (!page.pageInfo.hasNextPage || !nextCursor) return 'missing'
          if (nextCursor === cursor) return 'failed'
          cursor = nextCursor
        }
        return 'cancelled'
      } catch (error) {
        if (controller.signal.aborted || selectionRef.current !== selection) {
          return 'cancelled'
        }
        if (isErrorLike(error)) setPageError({ selection, error })
        return 'failed'
      } finally {
        if (pendingActionRef.current === action) pendingActionRef.current = null
        if (selectionRef.current === selection) {
          setActivity((current) =>
            current?.selection === selection && current.value === 'paging'
              ? null
              : current,
          )
        }
      }
    },
    [connection, endCursor, fetchPage, hasNextPage, query.loading, selection],
  )

  const loadMore = useCallback(async (): Promise<void> => {
    await loadPages()
  }, [loadPages])

  const beginWrite = (): boolean => {
    if (!selection || selectionRef.current !== selection) return false
    if (writeLockRef.current) return false
    const action = pendingActionRef.current
    if (action?.selection === selection && action.value === 'refreshing') {
      return false
    }
    if (query.loading && action?.value !== 'paging') return false
    writeLockRef.current = selection
    if (action?.selection === selection && action.value === 'paging') {
      action.controller.abort()
      pendingActionRef.current = null
      setActivity(null)
    }
    return true
  }

  const endWrite = (): void => {
    if (writeLockRef.current === selection) writeLockRef.current = null
  }

  if (!selection) return { status: 'idle' }
  if (deferRead) return { status: 'loading' }

  const scopedRequestError =
    requestError?.selection === selection ? requestError.error : undefined
  const scopedPageError =
    pageError?.selection === selection ? pageError.error : undefined
  const activeValue =
    activity?.selection === selection ? activity.value : undefined
  const rootQueryError =
    query.error &&
    CombinedGraphQLErrors.is(query.error) &&
    query.error.errors.some(isRootNotesError)
      ? query.error
      : undefined
  const rootQueryFailure = rootQueryError
    ? classifyNotesError(rootQueryError)
    : undefined
  if (rootQueryFailure) {
    return {
      status: 'failed',
      reason: rootQueryFailure,
      retry: requestFirstPage,
    }
  }

  const visibleError =
    scopedRequestError ?? (scopedPageError ? undefined : query.error)
  const failure = visibleError ? classifyNotesError(visibleError) : undefined
  if (failure) {
    return { status: 'failed', reason: failure, retry: requestFirstPage }
  }

  if (!connection) {
    if (query.loading) return { status: 'loading' }
    return {
      status: 'failed',
      reason: 'requestFailed',
      retry: requestFirstPage,
    }
  }

  return {
    status: 'ready',
    notes: connection.edges
      .filter(
        ({ node }, index, edges) =>
          edges.findIndex((edge) => edge.node.id === node.id) === index,
      )
      .map(({ node }) => node),
    hasNextPage,
    fetchingMore: activeValue === 'paging',
    pageError: scopedPageError,
    loadMore,
    loadUntilNote: loadPages,
    refresh: requestFirstPage,
    refreshing: activeValue === 'refreshing',
    warning: visibleError,
    readBusy:
      (query.loading && activeValue !== 'paging') ||
      activeValue === 'refreshing',
    beginWrite,
    endWrite,
  }
}
